
import { useState, useRef, useEffect } from 'react';

interface LazyImageProps {
  src: string;
  alt: string;
  className?: string;
  width?: number;
  height?: number;
  placeholder?: string;
  onLoad?: () => void;
  sizes?: string;
  srcSet?: string;
}

const LazyImage = ({ 
  src, 
  alt, 
  className = '', 
  width, 
  height, 
  placeholder,
  onLoad,
  sizes = "(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw",
  srcSet
}: LazyImageProps) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [isInView, setIsInView] = useState(false);
  const imgRef = useRef<HTMLImageElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsInView(true);
          observer.disconnect();
        }
      },
      { 
        threshold: 0.1,
        rootMargin: '50px' // Start loading 50px before entering viewport
      }
    );

    if (imgRef.current) {
      observer.observe(imgRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const handleLoad = () => {
    setIsLoaded(true);
    onLoad?.();
  };

  const handleError = () => {
    console.warn(`Failed to load image: ${src}`);
    setIsLoaded(true); // Still set as loaded to remove skeleton
  };

  // Generate responsive srcSet if not provided
  const generateSrcSet = (baseSrc: string) => {
    if (srcSet) return srcSet;
    
    const baseUrl = baseSrc.split('?')[0];
    const params = new URLSearchParams(baseSrc.split('?')[1] || '');
    
    return [
      `${baseUrl}?${params}&w=640&q=75 640w`,
      `${baseUrl}?${params}&w=750&q=75 750w`,
      `${baseUrl}?${params}&w=828&q=75 828w`,
      `${baseUrl}?${params}&w=1080&q=75 1080w`,
      `${baseUrl}?${params}&w=1200&q=75 1200w`,
      `${baseUrl}?${params}&w=1920&q=75 1920w`,
    ].join(', ');
  };

  return (
    <div 
      ref={imgRef}
      className={`relative overflow-hidden gpu-accelerated ${className}`}
      style={{ 
        width: width ? `${width}px` : '100%',
        height: height ? `${height}px` : 'auto',
        aspectRatio: width && height ? `${width} / ${height}` : undefined
      }}
      data-testid="lazy-image-container"
    >
      {/* Placeholder */}
      {!isLoaded && (
        <div 
          className="absolute inset-0 bg-gray-200 animate-pulse flex items-center justify-center"
          style={{ 
            backgroundImage: placeholder ? `url(${placeholder})` : undefined,
            backgroundSize: 'cover',
            backgroundPosition: 'center'
          }}
          aria-hidden="true"
        >
          {!placeholder && (
            <div className="w-8 h-8 border-2 border-gray-400 border-t-transparent rounded-full animate-spin"></div>
          )}
        </div>
      )}
      
      {/* Actual Image */}
      {isInView && (
        <picture>
          <source type="image/webp" srcSet={generateSrcSet(src.replace(/\.(jpg|jpeg|png)/, '.webp'))} sizes={sizes} />
          <img
            src={src}
            srcSet={srcSet || generateSrcSet(src)}
            sizes={sizes}
            alt={alt}
            width={width}
            height={height}
            className={`transition-opacity duration-300 w-full h-full object-cover ${isLoaded ? 'opacity-100' : 'opacity-0'}`}
            onLoad={handleLoad}
            onError={handleError}
            loading="lazy"
            decoding="async"
            fetchPriority="auto"
            style={{ 
              objectFit: 'cover',
              aspectRatio: width && height ? `${width} / ${height}` : undefined
            }}
          />
        </picture>
      )}
    </div>
  );
};

export default LazyImage;
